'use client';

import { c as _c } from "react/compiler-runtime";
import { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { NotificationThread } from '../../lib/threading';
import { carriedDays } from '../../lib/days';
import { NotificationRow } from './notification-row';

/**
 * What refuses to be folded into a day.
 *
 * The tasks-week decision warns that a day-first screen reads well on a tidy
 * desk and hides the problem on a messy one: a 178 day old item becomes four
 * words on one line and is never mentioned again. This strip is the answer,
 * and it is a promise rather than a heuristic. An
 * unfinished `now` older than today is lifted out of its day, pinned above
 * every day, and sorted OLDEST FIRST, which is the one place on the surface
 * where time runs backwards. On the seeded day that is six things and the top
 * of the list is a client payment twenty-seven days overdue.
 *
 * It never collapses to a count. It can be worked down to nothing, which is
 * the only way it goes away.
 */
export function CarriedStrip(t0) {
  const $ = _c(22);
  const {
    threads,
    dense: t1,
    limit
  } = t0;
  const dense = t1 === undefined ? false : t1;
  const [expanded, setExpanded] = useState(false);
  if (threads.length === 0) {
    return null;
  }
  const cap = limit ?? threads.length;
  let t2;
  if ($[0] !== cap || $[1] !== expanded || $[2] !== threads) {
    t2 = expanded ? threads : threads.slice(0, cap);
    $[0] = cap;
    $[1] = expanded;
    $[2] = threads;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const shown = t2;
  const hidden = threads.length - shown.length;
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = cn("rounded-2xl bg-white dark:bg-white/12", "shadow-[0_1px_3px_rgba(15,23,42,0.05),0_0_0_1px_rgba(244,63,94,0.16)]");
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-rose-600 dark:text-rose-400">Carried</span>;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const t5 = threads.length === 1 ? "one thing older than today is still blocked on you" : `${threads.length} things older than today are still blocked on you`;
  let t6;
  if ($[6] !== t5) {
    t6 = <header className="flex items-baseline gap-2 px-3 pb-1 pt-2.5">{t4}<span className="text-[11px] text-[#44546F] dark:text-white/70">{t5}</span></header>;
    $[6] = t5;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== dense || $[9] !== shown) {
    let t8;
    if ($[11] !== dense) {
      t8 = thread => <div key={thread.key} className="flex items-start gap-1"><span className="w-11 shrink-0 pt-2 text-right text-[10px] font-medium tabular-nums text-rose-600 dark:text-rose-400">{carriedDays(thread, new Date())}d</span><div className="min-w-0 flex-1"><NotificationRow thread={thread} dense={dense} /></div></div>;
      $[11] = dense;
      $[12] = t8;
    } else {
      t8 = $[12];
    }
    t7 = shown.map(t8);
    $[8] = dense;
    $[9] = shown;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[13] !== t7) {
    t8 = <div className="px-1.5 pb-1.5">{t7}</div>;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== hidden || $[16] !== threads) {
    t9 = hidden > 0 ? <button type="button" onClick={() => setExpanded(true)} className="flex w-full items-center justify-center gap-1 rounded-b-2xl px-3 py-1.5 text-[11px] font-medium text-[#44546F] dark:text-white/70 transition-colors hover:bg-white/60 dark:hover:bg-white/6 hover:text-[#091E42] dark:hover:text-white"><ChevronDown className="h-3 w-3" />{hidden} more carried, the oldest {carriedDays(threads[threads.length - 1], new Date())} days back</button> : null;
    $[15] = hidden;
    $[16] = threads;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== t6 || $[19] !== t8 || $[20] !== t9) {
    t10 = <section className={t3}>{t6}{t8}{t9}</section>;
    $[18] = t6;
    $[19] = t8;
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  return t10;
}